import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { authApi, ApiError } from "@/api";
import type { User } from "@/types";
import {
  getStaffToken,
  setStaffToken,
  clearStaffToken,
} from "@/lib/session";
import { clearDashboardCache } from "@/lib/dashboardCache";
import type { UserRole } from "@/types";

/**
 * Staff auth state. Flow (spec §3): login → store token → auth/me → only
 * treat the user as staff if their role grants console access. The gate lives
 * here so it is enforced in the UI, not just by the server.
 */

/** Roles allowed into the staff console. */
const STAFF_ROLES: ReadonlyArray<UserRole> = ["admin", "staff"];
const canAccessStaff = (role?: UserRole): boolean =>
  !!role && STAFF_ROLES.includes(role);

/** Roles allowed into admin-only areas of the console. Everything staff can
 *  reach is open to both roles; this narrows the handful of areas that are not
 *  (today: Review — the duplicate/merge reconciliation queues). */
const isAdminRole = (role?: UserRole): boolean => role === "admin";

interface AuthState {
  user: User | null;
  /** True only when authenticated AND the role grants console access. */
  hasStaffAccess: boolean;
  /** True only for role "admin" — gates the Review section (see navConfig). */
  isAdmin: boolean;
  loading: boolean;
  error?: string;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(!!getStaffToken());
  const [error, setError] = useState<string | undefined>(undefined);

  const loadMe = useCallback(async () => {
    if (!getStaffToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const me = await authApi.me();
      setUser(me);
    } catch (err) {
      // Bad/expired token — drop it.
      if (err instanceof ApiError && err.isAuthError) clearStaffToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Resume an existing session on first mount.
  useEffect(() => {
    void loadMe();
  }, [loadMe]);

  const login = useCallback(async (email: string, password: string) => {
    setError(undefined);
    setLoading(true);
    try {
      const { authToken } = await authApi.login(email, password);
      setStaffToken(authToken);
      const me = await authApi.me();
      setUser(me);
      if (!canAccessStaff(me.role)) {
        // Authenticated but not staff — refuse and clear (spec §3).
        clearStaffToken();
        setUser(null);
        setError("This account doesn’t have staff access.");
      }
    } catch (err) {
      clearStaffToken();
      setUser(null);
      setError(
        err instanceof ApiError
          ? err.status === 401 || err.status === 403
            ? "Incorrect email or password."
            : err.message
          : "Could not sign in.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    clearStaffToken();
    clearDashboardCache(); // studio figures must not outlive the session on a shared machine
    setUser(null);
    setError(undefined);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      hasStaffAccess: canAccessStaff(user?.role),
      isAdmin: isAdminRole(user?.role),
      loading,
      error,
      login,
      logout,
    }),
    [user, loading, error, login, logout],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
